import { z } from "zod";

/**
 * Canal par lequel la signature est arrivée.
 *
 * `device` : le signataire a signé sur le téléphone du poseur, sur place.
 * `email_link` : il a signé depuis son propre appareil, par un lien reçu par
 * email (voir types/signature-request.ts).
 *
 * Les deux chemins écrivent la MÊME ligne média et le même `mediaClientId` —
 * c'est la preuve qui diffère, pas la signature.
 */
export const SIGNATURE_CHANNELS = ["device", "email_link"] as const;
export type SignatureChannel = (typeof SIGNATURE_CHANNELS)[number];

/**
 * Traces d'exécution de la signature : c'est ce qui fait du dessin une preuve.
 *
 * Une image de signature seule ne prouve rien — n'importe qui peut la tracer.
 * Ce qui la rattache à une personne et à un instant, c'est le contexte :
 * l'appareil utilisé, l'adresse IP d'où elle est partie, et les horaires de la
 * session (ouverture du document → signature). C'est la « fiabilité du procédé »
 * que l'article 1367 du code civil demande à une signature électronique simple,
 * et la valeur probatoire que l'eIDAS art. 25.1 lui reconnaît.
 *
 * Tous les champs sauf `channel` sont optionnels : on n'invente pas une preuve
 * qu'on n'a pas. Une signature prise sur l'appareil du poseur, hors ligne, n'a
 * pas d'IP crédible — l'api ne verrait que celle de la synchronisation, des
 * heures plus tard et depuis un autre réseau. Mieux vaut un champ vide qu'un
 * champ faux dans un document qui sert de preuve.
 */
export const taskSignatureProofSchema = z.object({
  channel: z.enum(SIGNATURE_CHANNELS),
  /** IP du signataire au moment de la signature. `email_link` seulement. */
  ip: z.string().max(64).optional(),
  /** User-agent brut de l'appareil signataire. */
  userAgent: z.string().max(400).optional(),
  /** Demande de signature à l'origine du lien. `email_link` seulement. */
  requestId: z.string().uuid().optional(),
  /** Adresse à laquelle le lien a été envoyé. */
  sentTo: z.string().max(320).optional(),
  /** Envoi du lien. */
  sentAt: z.string().datetime({ offset: true }).optional(),
  /** Première ouverture du document par le signataire — début de la session. */
  openedAt: z.string().datetime({ offset: true }).optional(),
});
export type TaskSignatureProof = z.infer<typeof taskSignatureProofSchema>;

/**
 * Shared signature/meta shape used across task payload types (VT, PV de
 * réception, …). Extracted so every task type captures a signature with the
 * exact same fields: the media reference for the drawn signature image, the
 * timestamp, the signatory's declared name, best-effort GPS at signing time,
 * and the proof trail (see `taskSignatureProofSchema`).
 */
export const taskSignatureMetaSchema = z.object({
  mediaClientId: z.string().min(1),
  signedAt: z.string().datetime({ offset: true }),
  signatoryName: z.string().min(1).max(200),
  gpsLatitude: z.number().nullable().optional(),
  gpsLongitude: z.number().nullable().optional(),
  gpsAccuracy: z.number().nullable().optional(),
  proof: taskSignatureProofSchema.optional(),
});
export type TaskSignatureMeta = z.infer<typeof taskSignatureMetaSchema>;

/** Variant used for external signatories (e.g. the client) who may also leave an email. */
export const taskSignatureWithEmailSchema = taskSignatureMetaSchema.extend({
  signatoryEmail: z.string().email().optional(),
});
export type TaskSignatureWithEmail = z.infer<
  typeof taskSignatureWithEmailSchema
>;
